<template>
  <VDropdownWithKeyboardNavigation
    :id="id"
    :values="values"
    :isLast="isLast"
    :popperClass="['team-member-select__dropdown', popperClass].filter(Boolean).join(' ')"
    :className="['team-member-select', className].filter(Boolean).join(' ')"
    :disableHidingBySelect="true"
    :resetPaddingForItem="true"
    iconSize="md"
    @onApplyShow="onDropdownShow"
    @onApplyHide="onDropdownHide"
    @onScroll="$emit('scroll')"
  >
    <template #label>
      <slot
        name="label"
        :selectedUsers="selectedUsers"
        :removeUser="removeUser"
      >
        <div
          role="button"
          tabindex="0"
          class="team-member-select__field"
          :class="{ 'is-empty': !selectedUsers.length }"
        >
          <span
            v-if="selectedUsers.length"
            class="team-member-select__chips"
          >
            <span
              v-for="user in selectedUsers"
              :key="user.id"
              class="team-member-select__chip"
            >
              <Avatar
                :src="getUserPicture(user)"
                :gap="getUserInitials(user)"
                :type="isAllTeamsUser(user) ? 'teamsGroup' : 'avatarGroup'"
                size="sm"
              />
              <span class="team-member-select__chip-name body-text-medium-400">
                {{ getUserName(user) }}
              </span>
              <button
                type="button"
                class="team-member-select__chip-remove"
                @click.stop="removeUser(user)"
              >
                <icon
                  data="@icon/closeWithBg.svg"
                  :fill="true"
                  original
                  width="var(--icon-16)"
                  height="var(--icon-16)"
                />
              </button>
            </span>
          </span>
        </div>
      </slot>
    </template>

    <template #before-list>
      <div
        v-if="showSearch && !selectedTeamList"
        class="team-member-select__search"
      >
        <icon
          data="@icon/loupe.svg"
          :fill="false"
          width="var(--icon-24)"
          height="var(--icon-24)"
          color="var(--monochrome-05)"
        />
        <input
          ref="searchInput"
          :value="searchText"
          type="search"
          :placeholder="searchPlaceholder"
          class="team-member-select__search-input body-text-large-400"
          autocomplete="off"
          @input="onSearchInput"
        />
      </div>
    </template>

    <template #item="{ value }">
      <button
        type="button"
        class="team-member-select__item"
        :class="{ 'is-selected': isSelected(value) }"
        @click.stop="onSelect(value)"
      >
        <span class="team-member-select__item-content">
          <Avatar
            :src="getOptionPicture(value)"
            :gap="getOptionInitials(value)"
            :type="selectedTeamList || isAllTeamsUser(value) ? 'teamsGroup' : 'avatarGroup'"
            size="sm"
          />
          <span class="team-member-select__item-name body-text-large-400">
            {{ getOptionName(value) }}
          </span>
        </span>
        <icon
          v-if="isSelected(value)"
          data="@icon/check.svg"
          :fill="true"
          color="var(--serviceDone--02)"
          width="var(--icon-24)"
          height="var(--icon-24)"
          class="team-member-select__check"
        />
      </button>
    </template>

    <template
      v-if="allowTeamSelection"
      #after-list
    >
      <button
        type="button"
        class="team-member-select__change-list button-level-medium"
        @click="toggleList"
      >
        <span class="team-member-select__change-list-text">
          {{ changeListLabel }}
        </span>
      </button>
    </template>
  </VDropdownWithKeyboardNavigation>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue"

import Avatar from "@/components/Avatar/index.vue"
import VDropdownWithKeyboardNavigation from "@/components/Dropdown/VDropdownWithKeyboardNavigation.vue"

import { Team, User } from "@/models"
import { $t } from "@/plugins/i18n"

/**
 * Reusable team member picker for ToDo ownership and Team Schedule time-off forms.
 * It renders the shared dropdown UI while callers keep the existing selection rules.
 */
interface TeamMemberSelectProps {
  id: string
  users: User[]
  selectedUsers: User[]
  teams?: Team[]
  selectedTeams?: Team[]
  selectedTeamList?: boolean
  allowTeamSelection?: boolean
  showSearch?: boolean
  searchText?: string
  searchPlaceholder?: string
  isLast?: boolean
  className?: string
  popperClass?: string
}

const props = withDefaults(defineProps<TeamMemberSelectProps>(), {
  teams: () => [],
  selectedTeams: () => [],
  selectedTeamList: false,
  allowTeamSelection: true,
  showSearch: true,
  searchText: "",
  searchPlaceholder: $t("teamMemberSelect.searchPlaceholder"),
  isLast: true,
  className: "",
  popperClass: ""
})

const emit = defineEmits<{
  (e: "selectUser", value: User): void
  (e: "selectTeam", value: Team): void
  (e: "removeUser", value: User): void
  (e: "toggleList"): void
  (e: "search", value: string): void
  (e: "scroll"): void
}>()

const values = computed(() => (props.selectedTeamList ? props.teams : props.users))
const searchInput = ref<HTMLInputElement | null>(null)
const changeListLabel = computed(() =>
  props.selectedTeamList ? $t("teamMemberSelect.orSelectUser") : $t("teamMemberSelect.orSelectTeam")
)

const isAllTeamsUser = (value: User | Team) => value instanceof User && value.id === "all"

const getUserName = (user: User) => user.fullName || user.username

const getUserPicture = (user: User) => (isAllTeamsUser(user) ? null : user.pictureUrl || null)

const getUserInitials = (user: User) => (isAllTeamsUser(user) ? "" : user.getInitials())

const getOptionName = (value: User | Team) =>
  value instanceof Team ? value.name : getUserName(value)

const getOptionPicture = (value: User | Team) => {
  if (value instanceof Team) {
    return value.picture?.pictureUrl || null
  }

  if (isAllTeamsUser(value)) {
    return null
  }

  return value.pictureUrl || null
}

const getOptionInitials = (value: User | Team) => {
  if (value instanceof Team || isAllTeamsUser(value)) {
    return ""
  }

  return value.getInitials()
}

/** Treats the aggregate All team value as a selection of every visible team member. */
const isSelected = (value: User | Team) => {
  if (value instanceof User && props.selectedUsers.some(isAllTeamsUser)) {
    return true
  }

  const list = value instanceof Team ? props.selectedTeams : props.selectedUsers
  return list.some(item => item.id === value.id)
}

const onSelect = (value: User | Team) => {
  if (value instanceof Team) {
    emit("selectTeam", value)
    return
  }

  emit("selectUser", value)
}

const removeUser = (user: User) => {
  emit("removeUser", user)
}

const toggleList = () => {
  emit("toggleList")
}

const onSearchInput = (event: Event) => {
  const target = event.target as HTMLInputElement | null
  emit("search", target?.value ?? "")
}

/** Keeps the server-backed picker ready for input on open and resets its results on close. */
const onDropdownShow = async () => {
  await nextTick()
  searchInput.value?.focus()
}

const onDropdownHide = () => {
  emit("search", "")
}
</script>

<style lang="scss">
@import "@/assets/scss/utils/mixins/flex";

.team-member-select {
  width: fit-content;

  &__field {
    @include flex($align: flex-start, $justify: flex-start);

    width: 100%;
    min-height: 48px;
    padding: var(--step2);
    border: 1px solid var(--accent-03);
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    text-align: left;
  }

  &__chips {
    @include flex($align: flex-start, $justify: flex-start);

    flex-wrap: wrap;
    gap: var(--step2);
  }

  &__chip {
    @include flex;

    max-width: 100%;
    padding: var(--step1);
    border: 1px solid var(--monochrome-02);
    border-radius: 24px;
    gap: var(--step2);
  }

  &__chip-name,
  &__item-name {
    overflow: hidden;
    min-width: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chip-name {
    color: var(--monochrome-06);
  }

  &__chip-remove {
    @include flex;

    flex: 0 0 auto;
  }

  &__dropdown {
    .v-popper__inner {
      width: 352px;
      padding: var(--step4);
      padding-right: 0;
      border: 1px solid var(--monochrome-02);
      border-radius: var(--radius-section);
      background: var(--monochrome-00);
      box-shadow: 0 0 8px var(--shadow);
    }

    .dropdown-menu__list {
      max-height: 392px;
      padding-top: var(--step6);
      margin-right: 0;
      gap: var(--step2);
    }

    .dropdown-menu__item {
      padding: 0;
      border-radius: 4px;
    }
  }

  &__search {
    @include flex;

    height: 48px;
    padding: var(--step3) var(--step4);
    border: 1px solid var(--monochrome-03);
    border-radius: 4px;
    margin-right: var(--step4);
    background: var(--monochrome-01);
    gap: var(--step2);
  }

  &__search-input {
    min-width: 0;
    flex: 1;
    border: none;
    background: transparent;
    color: var(--monochrome-06);
    outline: none;

    &::placeholder {
      color: var(--monochrome-05);
    }
  }

  &__item {
    @include flex($justify: space-between);

    width: 100%;
    height: 48px;
    padding: var(--step2) var(--step4);
    border: 1px solid var(--monochrome-02);
    border-radius: 4px;
    background: var(--monochrome-00);
    color: var(--monochrome-06);
    cursor: pointer;
    gap: var(--step2);
    text-align: left;

    &.is-selected {
      border-color: var(--accent-03);
    }

    &:hover {
      background: var(--monochrome-01);
    }
  }

  &__item-content {
    @include flex;

    overflow: hidden;
    min-width: 0;
    gap: var(--step4);
  }

  &__item-name {
    color: var(--monochrome-06);
  }

  &__check {
    flex: 0 0 auto;
  }

  &__change-list {
    @include flex;

    width: 100%;
    height: 24px;
    padding-right: var(--step4);
    margin-top: var(--step6);
    color: var(--accent-03);
    gap: var(--step2);

    &::before,
    &::after {
      width: 100%;
      height: 1px;
      background: var(--accent-03);
      content: "";
    }

    &:hover {
      color: var(--accent-02);

      &::before,
      &::after {
        background: var(--accent-02);
      }
    }
  }

  &__change-list-text {
    flex-shrink: 0;
  }
}

.dark {
  .team-member-select {
    &__dropdown {
      .v-popper__inner {
        border-color: var(--monochrome-30);
        background: var(--monochrome-10);
        box-shadow: var(--center-02);
      }
    }

    &__search {
      border-color: var(--monochrome-03);
      background: var(--monochrome-16);

      .svg-icon {
        color: var(--monochrome-04) !important;
      }
    }

    &__search-input::placeholder {
      color: var(--monochrome-04);
    }

    &__item {
      border-color: var(--monochrome-30);
      background: var(--monochrome-10);

      &.is-selected {
        border-color: var(--accent-200);
      }

      &:hover {
        border-color: var(--monochrome-36);
        background: var(--monochrome-16);
      }
    }

    &__field,
    &__chip {
      border-color: var(--monochrome-26);
      background: var(--monochrome-20);
    }

    &__field {
      border-color: var(--accent-03);
    }

    &__change-list {
      color: var(--accent-200);

      &::before,
      &::after {
        background: var(--accent-02);
      }

      &:hover {
        color: var(--accent-100);

        &::before,
        &::after {
          background: var(--accent-100);
        }
      }
    }
  }
}
</style>
